import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart3,
  CheckCircle,
  Clock,
  CreditCard,
  Database,
  Edit,
  Key,
  Loader2,
  Mail,
  Plus,
  Save,
  Send,
  ShieldAlert,
  User,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SiRazorpay, SiStripe } from "react-icons/si";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchSendingSettings } from "../Redux/ReduxSlices/analyticsDashboardSlice";
import { fetchPaymentHistory } from "../Redux/ReduxSlices/paymentSlice";
import {
  fetchUserDetails,
  updateUserDetails,
} from "../Redux/ReduxSlices/profileSlice";
import { toast } from "sonner";
import moment from "moment-timezone";

import gmailIcon from "../assets/images/gmail.webp";
import microsoftIcon from "../assets/images/outlook.webp";
import yahooIcon from "../assets/images/yahoopng.png";
import zohoIcon from "../assets/images/zohomail.png";
import smtp from "../assets/images/smtp.svg";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EmailConnectModal from "../components/campaigns/EmailConnectModal";

// Mock user data
const EMPTY_PROFILE = {
  name: "",
  email: "",
  phone: "",
  company: "",
  jobTitle: "",
  location: "",
  bio: "",
  avatar: "",
  timezone: "Asia/Kolkata",
  language: "English",
};

export default function AccountPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { details: userData } = useSelector((state) => state.profile);

  const { sendingSettings, loading, error } = useSelector(
    (state) => state.campaignAnalytics,
  );

  const {
    data: paymentResp,
    loading: paymentHistoryLoading,
    error: paymentHistoryError,
  } = useSelector((state) => state.userPayment);

  const paymentItems = paymentResp?.data ?? [];

  const fileInputRef = useRef(null);
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showEmailConnect, setShowEmailConnect] = useState(false);
  const [selectedTab, setSelectedTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(EMPTY_PROFILE);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(fetchSendingSettings());
    dispatch(fetchPaymentHistory());
  }, [dispatch]);

  useEffect(() => {
    if (userData?.data) {
      setFormData({
        name: userData.data.name ?? "",
        email: userData.data.email ?? "",
        phone: userData.data.phone ?? "",
        company: userData.data.company ?? "",
        jobTitle: userData.data.job_title ?? "",
        location: userData.data.location ?? "",
        bio: userData.data.bio ?? "",
        avatar: userData.data.avatar_url ?? "",
        timezone: userData.data.timezone ?? "Asia/Kolkata",
        language: userData.data.language ?? "English",
      });
    }
  }, [userData]);

  useEffect(() => {
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    // Set the appropriate tab based on the route
    if (location.pathname === "/billing") {
      setSelectedTab("billing");
    } else if (location.pathname === "/settings") {
      setSelectedTab("profile");
    }
  }, [location.pathname]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const providers = Object.entries(userData?.provider_summary || {});

  const providerImages = {
    gmail: gmailIcon,
    outlook: microsoftIcon,
    zoho: zohoIcon,
    yahoo: yahooIcon,
  };

  if (!user) {
    return null;
  }

  const handleSave = () => {
    const payload = {
      name: formData.name,
      phone: formData.phone,
      company: formData.company,
      job_title: formData.jobTitle,
      location: formData.location,
      bio: formData.bio,
      avatar_url: formData.avatar || "",
      timezone: formData.timezone || "Asia/Kolkata",
      language: formData.language || "English",
    };

    setIsSaving(true);

    dispatch(updateUserDetails(payload))
      .unwrap()
      .then(() => {
        setIsEditing(false);
        // maybe toast success message
        toast.success("Profile Updated Successfull!");
        dispatch(fetchUserDetails());
      })
      .catch((err) => {
        toast.error("error", errMessage);
        // maybe toast error
      })
      .finally(() => {
        setIsSaving(false); // 🔴 stop loader always
      });
  };

  const handleCancel = () => {
    setFormData(mockUser);
    setIsEditing(false);
  };

  const formatDateTimeIST = (dateString, twelveHour = false) => {
    if (!dateString) return ""; // prevent error when null/undefined

    const date = new Date(dateString);
    if (isNaN(date)) return ""; // handle invalid date strings safely

    const options = {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: twelveHour,
    };

    return new Intl.DateTimeFormat("en-GB", options).format(date);
  };

  const handleUploadClick = () => {
    fileInputRef.current.click(); // trigger hidden input
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file); // local preview
      setFormData((prev) => ({
        ...prev,
        avatar: imageUrl,
      }));
    }
  };

  const handleRemovePhoto = () => {
    setFormData((prev) => ({
      ...prev,
      avatar: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // reset input
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 max-md:py-4">
        {/* Header */}
        <div className="mb-8">
          <h4 className="">Account Settings</h4>
          <p className="text-black mt-2 max-md:text-sm">
            Manage your account preferences and settings
          </p>
        </div>

        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          className=" w-full"
        >
          <TabsList className="grid grid-cols-3 bg-white/80 backdrop-blur-sm rounded-md shadow-lg border  border-black/20 max-md:h-10">
            <TabsTrigger
              value="profile"
              className="max-md:text-xs font-bold max-md:px-2 py-1.5 data-[state=active]:bg-gradient-to-r from-blue-600 to-indigo-600 data-[state=active]:text-white"
            >
              <div className="flex items-center justify-center gap-2">
                <User className="w-4 h-4" />
                <span className="font-semibold">Profile</span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="billing"
              className="max-md:text-xs font-bold max-md:px-2 py-1.5  data-[state=active]:bg-gradient-to-r from-blue-600 to-indigo-600 data-[state=active]:text-white"
            >
              <div className="flex items-center justify-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span className="font-semibold">Billing</span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="notifications"
              className="max-md:text-xs font-bold max-md:px-2 py-1.5  data-[state=active]:bg-gradient-to-r from-blue-600 to-indigo-600 data-[state=active]:text-white"
            >
              <div className="flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" />
                <span className="font-semibold">Mail Box</span>
              </div>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Profile Overview */}
              <Card className="lg:col-span-1 max-md:hidden">
                <CardHeader>
                  <CardTitle className="max-md:text-lg text-center">
                    Profile Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <Avatar className="w-24 h-24 mx-auto mb-4">
                    <AvatarImage src={formData.avatar || undefined} />
                    <AvatarFallback className="text-2xl">
                      {formData.email.slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <p className="font-bold text-lg">{formData.name}</p>
                  <p className="text-gray-600 mb-2">{formData.jobTitle}</p>
                  <p className="text-gray-600 mb-2">{formData.company}</p>
                  <p className="text-xs text-black font-semibold">
                    <span className=" text-black"> Last login: </span>
                    {userData?.data?.last_login
                      ? moment
                          .utc(userData.data.last_login)
                          .tz("Asia/Kolkata")
                          .format("DD MMM YYYY, hh:mm:ss A")
                      : "-"}
                  </p>

                  <div className="mt-6 space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Profile Details */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between max-md:gap-6">
                    <CardTitle className="max-md:text-lg">
                      Profile Information
                    </CardTitle>

                    <div className="flex flex-col items-end gap-2">
                      {!isEditing ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditing(true)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancel}
                            className="max-md:p-2"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setConfirmSaveOpen(true)}
                            disabled={isSaving}
                            className="main-button flex items-center justify-center gap-2 max-md:p-2"
                          >
                            {isSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Saving…</span>
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4" />
                                <span>Save</span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        // disabled={!isEditing}
                        disabled
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            phone: e.target.value,
                          }))
                        }
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            company: e.target.value,
                          }))
                        }
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <Label htmlFor="jobTitle">Job Title</Label>
                      <Input
                        id="jobTitle"
                        value={formData.jobTitle}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            jobTitle: e.target.value,
                          }))
                        }
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            location: e.target.value,
                          }))
                        }
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                  {/* <div>
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          bio: e.target.value,
                        }))
                      }
                      disabled={!isEditing}
                      rows={3}
                    />
                  </div> */}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="max-md:text-lg">
                  Connected Providers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {(() => {
                    // Filter only connected providers
                    const connectedProviders = [
                      "gmail",
                      "outlook",
                      "zoho",
                      "yahoo",
                      "smtp",
                    ]
                      .map((providerName) => {
                        const existingProvider = providers.find(
                          ([name]) => name === providerName,
                        );
                        const provider = existingProvider?.[1];
                        return provider?.connected
                          ? { name: providerName, ...provider }
                          : null;
                      })
                      .filter(Boolean);

                    // If no provider connected → show message
                    if (connectedProviders.length === 0) {
                      return (
                        <div className="col-span-full text-center text-gray-600 py-10 bg-gray-50 rounded-lg border border-dashed">
                          <p className="text-sm font-medium">
                            No provider connected. Please connect a provider to
                            continue.
                          </p>
                        </div>
                      );
                    }

                    // Render connected providers
                    return connectedProviders.map((provider) => (
                      <div
                        key={provider.name}
                        className="text-center p-4 rounded-lg shadow-sm "
                      >
                        {/* Provider Logo */}
                        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-2">
                          {providerImages[provider.name] ? (
                            <img
                              src={providerImages[provider.name]}
                              alt={`${provider.name} logo`}
                              className="h-8 w-8 object-contain"
                            />
                          ) : (
                            <Mail className="h-6 w-6 text-gray-600" />
                          )}
                        </div>

                        {/* Provider Name */}
                        <p className="text-lg font-semibold capitalize">
                          {provider.name}
                        </p>

                        {/* From Email */}
                        <p className="text-xs text-gray-600 text-wrap">
                          {provider.from_email}
                        </p>

                        {/* Expiry Date */}
                      </div>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"></div>

            {/* Billing History */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="max-md:text-lg">
                    Billing History
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="max-md:text-sm">
                {paymentHistoryLoading && <p>Loading…</p>}
                {paymentHistoryError && (
                  <p className="text-red-600">
                    Error: {String(paymentHistoryError)}
                  </p>
                )}

                {!paymentHistoryLoading && !paymentHistoryError && (
                  <>
                    {paymentItems.length > 0 ? (
                      <div className="space-y-3">
                        {[...paymentItems]
                          .sort(
                            (a, b) =>
                              new Date(b.created_at) - new Date(a.created_at),
                          )
                          .map((p, index) => {
                            const status = (p.status || "").toLowerCase();

                            const statusStyles = {
                              succeeded:
                                "bg-green-100 text-green-800 border-green-200",
                              captured:
                                "bg-green-100 text-green-800 border-green-200",
                              processing:
                                "bg-yellow-100 text-yellow-800 border-yellow-200",
                              pending:
                                "bg-yellow-100 text-yellow-800 border-yellow-200",
                              failed: "bg-red-100 text-red-800 border-red-200",
                            };
                            const statusClass =
                              statusStyles[status] ||
                              "bg-gray-100 text-gray-800 border-gray-200";

                            // ✅ Fix amount display
                            const amountText =
                              p.amount_display ??
                              (p.currency && p.amount != null
                                ? `${p.currency} ${(p.amount / 100).toFixed(2)}`
                                : "-");

                            // ✅ Fix gateway field
                            const gateway = (p.provider || "").toLowerCase();
                            const gatewayIcons = {
                              stripe: (
                                <SiStripe className="h-4 w-4 text-indigo-600" />
                              ),
                              razorpay: (
                                <SiRazorpay className="h-4 w-4 text-blue-600" />
                              ),
                            };
                            const gatewayLabel =
                              gateway === "razorpay"
                                ? "Razorpay"
                                : gateway === "stripe"
                                  ? "Stripe"
                                  : gateway;

                            const idPrimary =
                              p.order_id ||
                              p.subscription_pk ||
                              p.payment_id ||
                              p?.raw?.payment_intent_id;

                            // ✅ Access nested fields safely
                            const method = p.raw?.method || p.method;
                            const email =
                              p.raw?.email || p.email || p?.raw?.receipt_email;
                            const contact = p.raw?.contact || p.contact;
                            const plan = p.raw?.notes?.plan;
                            const fee = p.raw?.fee;
                            const tax = p.raw?.tax;
                            const paymnetId = p.payment_id;

                            return (
                              <div
                                key={p.payment_id ?? index}
                                className="group flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:shadow-md hover:border-indigo-200 transition-all duration-200 bg-white"
                              >
                                {/* Left side: details */}
                                <div className="flex items-start gap-4">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-semibold text-gray-900">
                                        {idPrimary}
                                      </p>

                                      {/* Gateway chip */}
                                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border bg-gray-50 text-gray-700 border-gray-200">
                                        {gatewayIcons[gateway]}
                                        {gatewayLabel}
                                      </span>

                                      {/* Method chip */}
                                      {method && (
                                        <span className="inline-flex items-center text-xs px-2 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                                          {method.toUpperCase()}
                                        </span>
                                      )}
                                    </div>

                                    <p className="text-sm text-gray-600 mt-0.5">
                                      {formatDate(p.created_at + "Z")}
                                    </p>

                                    <p className="text-xs text-gray-500 mt-1">
                                      {email ? email : "—"}
                                      {contact ? ` • ${contact}` : ""}
                                    </p>

                                    {/* Optional: Plan / Fee / Tax */}
                                    {/* <p className="text-xs text-gray-400 mt-0.5">
                                      Plan: {plan || "-"} | Fee: ₹
                                      {fee != null && !isNaN(fee)
                                        ? (fee / 100).toFixed(2)
                                        : "-"}{" "}
                                      | Tax: ₹
                                      {tax != null && !isNaN(tax)
                                        ? (tax / 100).toFixed(2)
                                        : "-"}
                                    </p> */}

                                    <p className="text-xs text-gray-400 mt-0.5">
                                      {paymnetId}
                                    </p>
                                  </div>
                                </div>

                                {/* Right side: amount + status + download */}
                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <p className="text-base font-semibold text-gray-900">
                                      {amountText}
                                    </p>
                                    <span
                                      className={`inline-flex items-center text-xs px-2 py-1 rounded-full border ${statusClass}`}
                                      title={`Status: ${p.status}`}
                                    >
                                      {[
                                        "captured",
                                        "succeeded",
                                        "success",
                                      ].includes(p.status?.toLowerCase())
                                        ? "Successful"
                                        : p.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">
                        No payment history found.
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            <Card className="overflow-hidden border-0 shadow-lg">
              <CardHeader className=" text-black pb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl flex items-center gap-2 max-md:text-lg">
                      <Mail className="h-6 w-6" />
                      Mail Box
                    </CardTitle>
                    <p className="text-black mt-2 max-md:text-sm">
                      Your current email sending settings and quota usage
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
                    <Send className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 space-y-6 -mt-4">
                {/* 🔹 Totals Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-gray-700 flex items-center gap-2 max-md:text-sm">
                        <Clock className="h-4 w-4 text-blue-600" />
                        Total Sent Today
                      </p>
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-700">
                          {sendingSettings?.totals?.sent_today || 0}
                        </span>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {sendingSettings?.totals?.sent_today || 0}
                    </p>
                    <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            100,
                            ((sendingSettings?.totals?.sent_today || 0) / 100) *
                              100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-gray-700 flex items-center gap-2 max-md:text-sm">
                        <BarChart3 className="h-4 w-4 text-purple-600" />
                        Lifetime Sent
                      </p>
                      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-purple-700">
                          {sendingSettings?.totals?.lifetime_sent || 0}
                        </span>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {sendingSettings?.totals?.lifetime_sent || 0}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      All-time emails sent
                    </p>
                  </div>
                </div>

                {/* 🔹 Providers Section Header */}
                <div className="flex items-center justify-between pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Database className="h-5 w-5 text-indigo-600" />
                    Email Providers
                  </h3>
                  <Badge
                    variant="outline"
                    className="bg-indigo-50 text-indigo-700 border-indigo-200"
                  >
                    {
                      Object.values(sendingSettings?.providers || {}).filter(
                        (provider) => provider?.connected === true,
                      ).length
                    }{" "}
                    Connected
                  </Badge>
                </div>

                {/* ✅ Filter connected providers only */}
                {sendingSettings?.providers &&
                Object.entries(sendingSettings.providers).some(
                  ([_, provider]) => provider.connected === true,
                ) ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(sendingSettings.providers)
                      .filter(([_, provider]) => provider.connected === true)
                      .map(([key, provider]) => (
                        <div
                          key={key}
                          className="border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
                        >
                          {/* Provider Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                  provider.connected
                                    ? "bg-green-100"
                                    : "bg-red-100"
                                }`}
                              >
                                {provider.connected ? (
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                ) : (
                                  <ShieldAlert className="h-5 w-5 text-red-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-semibold capitalize">
                                  {key}
                                </p>
                              </div>
                            </div>
                            <Badge
                              variant="secondary"
                              className={
                                provider.connected
                                  ? "bg-green-100 text-green-800 hover:bg-green-200"
                                  : "bg-gray-200 text-gray-700"
                              }
                            >
                              {provider.connected ? "Active" : "Inactive"}
                            </Badge>
                          </div>

                          {/* From Email */}
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-500 mb-1">
                              From Email
                            </p>
                            <p className="text-sm text-gray-900 font-mono truncate">
                              {provider.from_email || "Not configured"}
                            </p>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">
                                Emails Sent Today
                              </p>
                              <p className="text-lg font-bold text-gray-900">
                                {provider.sent_today}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">
                                Daily Quota Remaining
                              </p>
                              <p className="text-lg font-bold text-gray-900">
                                {provider.remaining_today}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                              <p className="text-xs text-gray-500 mb-1">
                                Lifetime Sent
                              </p>
                              <p className="text-lg font-bold text-gray-900">
                                {provider.lifetime_sent}
                              </p>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-4">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>Daily Usage</span>
                              <span>
                                {Math.round(
                                  (provider.sent_today /
                                    (provider.sent_today +
                                      provider.remaining_today)) *
                                    100,
                                )}
                                %
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  provider.sent_today /
                                    (provider.sent_today +
                                      provider.remaining_today) >
                                  0.8
                                    ? "bg-red-500"
                                    : provider.sent_today /
                                          (provider.sent_today +
                                            provider.remaining_today) >
                                        0.6
                                      ? "bg-yellow-500"
                                      : "bg-green-500"
                                }`}
                                style={{
                                  width: `${
                                    (provider.sent_today /
                                      (provider.sent_today +
                                        provider.remaining_today)) *
                                    100
                                  }%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  // 🔹 No connected provider state
                  <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl bg-gray-50">
                    <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No provider connected
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Please connect a provider to continue.
                    </p>
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      // onClick={() => (window.location.href = "/addemails")}
                      onClick={() => setShowEmailConnect(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Connect Provider
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <Dialog open={confirmSaveOpen} onOpenChange={setConfirmSaveOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle>Save Profile Changes</DialogTitle>
            <DialogDescription>
              Are you sure you want to save the changes made to your profile?
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmSaveOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>

            <Button
              className="main-button"
              onClick={() => {
                setConfirmSaveOpen(false);
                handleSave(); // ✅ call your existing save logic
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving…
                </>
              ) : (
                "Confirm & Save"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EmailConnectModal
        open={showEmailConnect}
        onClose={() => setShowEmailConnect(false)}
      />

      <Footer />
    </div>
  );
}
